import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    TEMPLATE_CARD_BODY_COLUMN_CLASS,
    TEMPLATE_CARD_CLASS,
    TEMPLATE_CARD_END_CLASS,
    TEMPLATE_CARD_HEADER_CLASS,
    TEMPLATE_CARD_ROW_CLASS,
    TEMPLATE_CARD_SUBTITLE_CLASS,
    TEMPLATE_CARD_TITLE_CLASS,
    TEMPLATE_REPLY_CLASS,
    TEMPLATE_STATUS_CLASS,
    TEMPLATE_TOKEN_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import { formatOutreachShift } from '@apps/web/components/phoebe_chat_view/tool_calls/detail_tool_ui';
import {
    ActionMetricGrid,
    CaregiverToolErrorCard,
    formatDetailStatus,
    formatToolFieldLabel,
    getStatusBadge,
    getStringArray,
    joinPresent,
    ToolDetailHeader,
    ToolDetailKeyValue,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { StatusBadge } from '@apps/web/components/ui/status_badge';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

export const CONVERSATION_CONTROL_TOOL_OUTPUTS = new Set<string>([
    'add_outreach_subscriber',
    'approve_staged_message',
    'edit_staged_message_content',
    'monitor_outreach',
    'notify',
    'override_contact_attempt_response',
    'reject_staged_message',
    'remove_outreach_subscriber',
    'review_outreach',
    'send_one_off_sms_in_conversation',
    'stop_agent_monitoring',
    'submit_outreach_suggestion_feedback',
]);

function getConversationControlArgs(
    data: Record<string, unknown>,
): Record<string, unknown> | null {
    return isRecord(data['__tool_args']) ? data['__tool_args'] : null;
}

export function formatCadenceLabel(seconds: number | null): string | null {
    if (seconds === null) {
        return null;
    }
    if (seconds >= 3600 && seconds % 3600 === 0) {
        const hours = seconds / 3600;
        return `${hours} hr cadence`;
    }
    if (seconds >= 60 && seconds % 60 === 0) {
        const minutes = seconds / 60;
        return `${minutes} min cadence`;
    }
    return `${seconds} sec cadence`;
}

function InlineToolId({ value }: { readonly value: string }) {
    return <span className="font-mono text-[11px] break-all">{value}</span>;
}

function MessagePreviewSection({
    title = 'Message',
    content,
}: {
    readonly title?: string;
    readonly content: string | null;
}) {
    if (!content) {
        return null;
    }
    return (
        <ToolTopicSection title={title}>
            <p className="text-foreground text-xs whitespace-pre-wrap">
                {content}
            </p>
        </ToolTopicSection>
    );
}

function getOutputOrArgString(
    data: Record<string, unknown>,
    args: Record<string, unknown> | null,
    outputKey: string,
    argKey = outputKey,
): string | null {
    return getStringValue(data, outputKey) ?? getStringValue(args, argKey);
}

function StopMonitoringControlCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const args = getConversationControlArgs(data);
    const reason =
        getStringValue(data, 'reason') ?? getStringValue(args, 'reason');
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Monitoring stopped"
                badges={[{ label: 'Stopped', tone: 'red' }]}
            />
            <MessagePreviewSection title="Reason" content={reason} />
        </div>
    );
}

function RemoveSubscriberControlCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const args = getConversationControlArgs(data);
    const subscriber =
        getStringValue(data, 'subscriber_name') ??
        getStringValue(args, 'user_name') ??
        getOutputOrArgString(data, args, 'user_id');
    const outreachId = getOutputOrArgString(data, args, 'outreach_id');
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Subscriber removed"
                badges={[{ label: 'Unsubscribed', tone: 'red' }]}
            />
            {subscriber || outreachId ? (
                <ToolTopicSection title="Subscriber">
                    <div className="space-y-1.5">
                        {subscriber ? (
                            <ToolDetailKeyValue
                                label="User"
                                value={subscriber}
                            />
                        ) : null}
                        {outreachId ? (
                            <ToolDetailKeyValue
                                label="Outreach"
                                value={<InlineToolId value={outreachId} />}
                            />
                        ) : null}
                    </div>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

// Dispatches the two control tools that don't have a dedicated *ActionCard
// branch in phoebe_chat_view.tsx. The remaining CONVERSATION_CONTROL_TOOL_OUTPUTS
// (monitor/review/notify/etc.) are caught earlier and render their *ActionCard.
export function ConversationControlCards({
    data,
    toolName,
}: {
    readonly data: Record<string, unknown>;
    readonly toolName: string;
}) {
    const error = getStringValue(data, 'error');
    if (error) {
        return (
            <CaregiverToolErrorCard
                title={`${formatToolFieldLabel(toolName)} needs review`}
                error={error}
            />
        );
    }

    if (toolName === 'stop_agent_monitoring') {
        return <StopMonitoringControlCard data={data} />;
    }
    if (toolName === 'remove_outreach_subscriber') {
        return <RemoveSubscriberControlCard data={data} />;
    }
    return null;
}

export function MonitoringActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const cadenceSeconds = getNumberValue(data, 'cadence_seconds');
    const nextRound = getNumberValue(data, 'next_round');
    const conditions = getStringArray(data['conditions']);
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Monitoring scheduled"
                subtitle={
                    cadenceSeconds !== null
                        ? `${Math.round(cadenceSeconds / 60)} min cadence`
                        : null
                }
                badges={[
                    ...(nextRound !== null
                        ? [
                              {
                                  label: `Round ${nextRound} next`,
                                  tone: 'blue' as const,
                              },
                          ]
                        : []),
                ]}
            />
            {conditions.length > 0 ? (
                <ToolTopicSection title="Conditions">
                    <div className="space-y-1">
                        {conditions.map((condition) => (
                            <p
                                key={condition}
                                className="text-foreground text-xs"
                            >
                                {condition}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function OutreachReviewActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const { formatName } = useFormatPersonName();
    const shifts = isRecord(data['shifts']) ? data['shifts'] : {};
    const filled = Array.isArray(shifts['filled']) ? shifts['filled'] : [];
    const pending = Array.isArray(shifts['pending']) ? shifts['pending'] : [];
    const unfilled = Array.isArray(shifts['unfilled'])
        ? shifts['unfilled']
        : [];
    const totalAttempts = isRecord(data['contact_attempts_summary'])
        ? getNumberValue(data['contact_attempts_summary'], 'total')
        : null;
    const availableCaregivers = pending
        .filter(isRecord)
        .flatMap((shift) =>
            Array.isArray(shift['available_caregivers'])
                ? shift['available_caregivers'].filter(isRecord)
                : [],
        );
    const caregiverResponses = pending
        .filter(isRecord)
        .flatMap((shift) =>
            Array.isArray(shift['caregiver_responses'])
                ? shift['caregiver_responses'].filter(isRecord)
                : [],
        );

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Outreach reviewed"
                subtitle={
                    totalAttempts !== null
                        ? `${totalAttempts} contact attempts`
                        : null
                }
            />
            <ActionMetricGrid
                metrics={[
                    { label: 'Filled', value: filled.length },
                    { label: 'Pending', value: pending.length },
                    { label: 'Unfilled', value: unfilled.length },
                ]}
            />
            <ToolTopicSection title="Shift status">
                <div className="space-y-1">
                    {[...filled, ...pending, ...unfilled]
                        .filter(isRecord)
                        .slice(0, 4)
                        .map((shift, index) => (
                            <p key={index} className="text-foreground text-xs">
                                {formatOutreachShift(shift, formatName)}
                            </p>
                        ))}
                </div>
            </ToolTopicSection>
            {availableCaregivers.length > 0 ? (
                <ToolTopicSection title="Available caregivers">
                    <div className="space-y-1">
                        {availableCaregivers.slice(0, 4).map((caregiver) => (
                            <p
                                key={
                                    getStringValue(caregiver, 'caregiver_id') ??
                                    getStringValue(
                                        caregiver,
                                        'caregiver_name',
                                    ) ??
                                    'available-caregiver'
                                }
                                className="text-foreground text-xs"
                            >
                                {formatName(
                                    getStringValue(caregiver, 'caregiver_name'),
                                ) || 'Caregiver'}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
            {caregiverResponses.length > 0 ? (
                <ToolTopicSection title="Caregiver responses">
                    <div className="space-y-1">
                        {caregiverResponses.slice(0, 4).map((response) => (
                            <p
                                key={
                                    joinPresent([
                                        getStringValue(
                                            response,
                                            'caregiver_name',
                                        ),
                                        getStringValue(response, 'response'),
                                    ]) ?? 'caregiver-response'
                                }
                                className="text-foreground text-xs"
                            >
                                {formatName(
                                    getStringValue(response, 'caregiver_name'),
                                ) || 'Caregiver'}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function NotificationActionCard({
    data,
    args,
}: {
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Notification sent"
                subtitle={getStringValue(args, 'title')}
                badges={getStatusBadge(getStringValue(data, 'level'), 'green')}
            />
            {getStringValue(args, 'message') ? (
                <ToolTopicSection title="Message">
                    <p className="text-foreground text-xs">
                        {getStringValue(args, 'message')}
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function ResponseOverrideActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Caregiver response updated"
                badges={getStatusBadge(getStringValue(data, 'status'), 'green')}
            />
            <ToolTopicSection title="Response">
                <p className="text-foreground text-xs">
                    {formatDetailStatus(getStringValue(data, 'response')) ??
                        'Not returned'}
                </p>
            </ToolTopicSection>
        </div>
    );
}

export function SuggestionFeedbackActionCard({
    data,
    args,
}: {
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Suggestion feedback recorded"
                badges={getStatusBadge(
                    getStringValue(data, 'sentiment'),
                    'green',
                )}
            />
            {getStringValue(args, 'reason') ? (
                <ToolTopicSection title="Reason">
                    <p className="text-foreground text-xs">
                        {getStringValue(args, 'reason')}
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}

export function SubscriberActionCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Subscriber added"
                badges={getStatusBadge(getStringValue(data, 'status'), 'green')}
            />
            <ToolTopicSection title="Subscriber">
                <p className="text-muted-foreground font-mono text-[11px]">
                    {getStringValue(data, 'user_id') ?? 'Not returned'}
                </p>
            </ToolTopicSection>
        </div>
    );
}

export function StagedMessageActionCard({
    toolName,
    data,
    args,
}: {
    readonly toolName: string;
    readonly data: Record<string, unknown>;
    readonly args: Record<string, unknown> | null;
}) {
    const { formatName } = useFormatPersonName();
    const title =
        toolName === 'approve_staged_message'
            ? 'Staged message approved'
            : toolName === 'reject_staged_message'
              ? 'Staged message rejected'
              : toolName === 'edit_staged_message_content'
                ? 'Staged message edited'
                : 'One-off SMS sent';
    const content =
        getStringValue(args, 'updated_content') ??
        getStringValue(args, 'content') ??
        getStringValue(args, 'reason');
    const recipientName = formatName(
        getStringValue(args, 'recipient_name') ??
            getStringValue(args, 'caregiver_name') ??
            getStringValue(data, 'recipient_name') ??
            getStringValue(data, 'caregiver_name'),
    );
    const status = getStringValue(data, 'status');
    const delivered =
        toolName === 'approve_staged_message' ||
        toolName === 'send_one_off_sms_in_conversation';
    const statusLabel = delivered
        ? 'Sent'
        : ((status ? formatDetailStatus(status) : null) ?? 'Updated');

    return (
        <div className={TEMPLATE_CARD_CLASS}>
            <div className={TEMPLATE_CARD_HEADER_CLASS}>
                <div className="min-w-0">
                    <p className={TEMPLATE_CARD_TITLE_CLASS}>Message draft</p>
                    <p className={TEMPLATE_CARD_SUBTITLE_CLASS}>{title}</p>
                </div>
                <div className={TEMPLATE_CARD_END_CLASS}>
                    <span className={TEMPLATE_TOKEN_CLASS}>SMS</span>
                </div>
            </div>
            <div className={TEMPLATE_CARD_BODY_COLUMN_CLASS}>
                {recipientName ? (
                    <p className="text-xs text-[#617578]">
                        To:{' '}
                        <span className="rounded-md bg-[#E9FCF0] px-1 py-0.5 font-medium text-[#05614F]">
                            {recipientName}
                        </span>
                    </p>
                ) : null}
                {content ? (
                    <div className={TEMPLATE_REPLY_CLASS}>{content}</div>
                ) : null}
            </div>
            <div className={TEMPLATE_CARD_ROW_CLASS}>
                <div className="min-w-0 flex-1">
                    <p className="font-medium">
                        {recipientName ?? 'Outgoing message'}
                    </p>
                    <p className="truncate text-xs text-[#617578]">
                        {getStringValue(data, 'message_id') ??
                            'Delivery update'}
                    </p>
                </div>
                <StatusBadge
                    status={{
                        label: statusLabel,
                        intent: delivered ? 'positive' : 'neutral',
                    }}
                    className={
                        TEMPLATE_STATUS_CLASS[
                            delivered ? 'positive' : 'neutral'
                        ]
                    }
                />
            </div>
        </div>
    );
}
